import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button, CircularProgress, Divider, Paper, Stack, TextField, Typography } from "@mui/material"
import { Box } from "@mui/material"
import { useMCContext } from "./contexts/useMCStates"
import { MCHeader } from "./Components/MCHeader";
import { EventCardSection } from "./EventCardSection";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import CreateApi from '../Components/Settings/appSettings';
import axios from 'axios';
import { StyledTextboxInput } from "./Components/StyledTextboxInput";
import { StyledPassword } from "./Components/StyledPassword";
import { StyledAutocompleteInput } from "./Components/StyledAutocompleteInput";
import stateAbbreviations from "./Settings/StateAbbreviations.json";
import PullToRefresh from "./services/PullToRefresh";
import loginBackground from '../assets/moment_login_background.jpg';


type UserData = {
    phoneNumber: string;
    birthDate: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    gender: string;
    ghinNumber: string;
}

export const LoginPostHandler = () => {

    const { windowSize, factor, appTheme } = useMCContext();
    const auth = useAuth();
    const navigate = useNavigate();
    const api = CreateApi();

    const [status, setStatus] = useState<'startup' | 'verifying' | 'creating' | 'connected' | 'error'>('startup');
    const [userData, setUserData] = useState<UserData>({
        phoneNumber: '',
        birthDate: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        gender: 'Select Gender',
        ghinNumber: '',
    });
    const [submitAllowed, setSubmitAllowed] = useState(false);
    const redirectURL = localStorage.getItem('redirectURL') || '/';
    const stackRef = useRef<HTMLDivElement>(null);

    // Component lifecycle logging
    useEffect(() => {
        console.log('=== LOGIN COMPONENT MOUNTED ===');
        console.log('Initial state:', { isLoading: auth.isLoading, isAuthenticated: auth.isAuthenticated, status });
    }, []);



    // Real verification logic - check if user exists in database
    const verifyAccount = useCallback(async () => {
        console.log('Starting account verification...');
        console.log('User profile:', auth.user?.profile);
        
        try {
            const user = await api.get(`/myaccount/GetOwnProfile`);

            if (!user.data) {
                console.log('User not found, setting status to creating');
                setStatus('creating');
            } else {
                console.log('User found, setting status to connected');
                setStatus('connected');
            }
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                console.log('User not found, setting status to creating');
                setStatus('creating');
            } else {
                console.error('Error verifying account:', error);
                setStatus('error');
            }
        }
    }, [api, auth.user?.profile?.sub]);



    // Simplified auth effect - only handle the verification flow
    useEffect(() => {
        console.log('LoginPostHandler auth effect - isLoading:', auth.isLoading, 'isAuthenticated:', auth.isAuthenticated, 'status:', status);
        
        // If user is authenticated and still in startup, move to verifying
        if (!auth.isLoading && auth.isAuthenticated && status === 'startup') {
            console.log('User authenticated, setting status to verifying');
            setStatus('verifying');
        }
    }, [auth.isLoading, auth.isAuthenticated, status]);

    // Status effect - handle status transitions
    useEffect(() => {
        console.log('Status effect - status:', status);

        if (status === 'verifying') {
            console.log('Status is verifying, calling verifyAccount...');
            verifyAccount();
        }
    }, [status, verifyAccount]);

    useEffect(() => {
        // Check if all required fields are valid
        const isPhoneNumberValid = userData.phoneNumber && userData.phoneNumber.length === 10;
        const isBirthDateValid = userData.birthDate && new Date(userData.birthDate) <= new Date();
        const isAddressValid = userData.address && userData.address.length >= 3;
        const isCityValid = userData.city && userData.city.length >= 3;
        const isStateValid = userData.state && userData.state !== '';
        const isZipCodeValid = userData.zipCode && userData.zipCode.length === 5;

        const allFieldsValid = isPhoneNumberValid && 
                             isBirthDateValid && 
                             isAddressValid && 
                             isCityValid && 
                             isStateValid && 
                             isZipCodeValid;

        if (allFieldsValid) {
            // All fields are valid, enable submit
            setSubmitAllowed(true);
        } else {
            // Some fields invalid, disable submit
            setSubmitAllowed(false);
        }

    }, [userData]); // Re-run when userData changes
    


    // Real account creation logic
    const createAccount = useCallback(async () => {
        console.log('Creating account...');
        console.log('User data:', userData);
        console.log('Auth profile:', auth.user?.profile);
        
        try {
            const authData = auth?.user?.profile;

            const user = await api.post('/mc/CreateUser', {
                userID: authData?.sub,
                userEmail: authData?.email?.toLowerCase(), 
                firstName: authData?.given_name?.trim(),
                lastName: authData?.family_name?.trim(),
                displayName: authData?.given_name?.trim() + " " + authData?.family_name?.trim(),
                membershipTier: 1,
                membershipType: 'Basic',
                accountTier: 1,
                accountType: 'Personal',
                ...userData
            });

            if (user.status === 200) {
                console.log('Account created successfully');
                setStatus('connected');
            } else {
                console.log('Account creation failed');
                setStatus('error');
            }
        } catch (error) {
            console.error('Error creating account:', error);
            setStatus('error');
        }
    }, [api, auth.user?.profile, userData]);

    const cleanPhoneNumber = (input: string): string => {
        // Remove all non-numeric characters using regex
        return input.replace(/[^0-9]/g, '');
    }

    const formatPhoneNumber = (input: string): string => {
        
        const match = input.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `(${match[1]}) ${match[2]}-${match[3]}`;
        }
        return input;
    }

    return (
       <Stack direction="column" sx={{ width: '100vw', height: '100vh',
            backgroundImage: `url(${loginBackground})`,
            backgroundSize: 'cover', 
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
       }}>
            <PullToRefresh threshold={80} resistance={2}  containerRef={stackRef as React.RefObject<HTMLDivElement>} 
            onRefresh={() => {
                // Remove the navigate(0) that was causing the refresh loop
                return Promise.resolve();
            }}>
            <MCHeader label="Login Verification" showCart={false} />
            
            <Box sx={{ 
                position: 'relative', 
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                padding: 2,
                
            }}>
                <Paper ref={stackRef} sx={{ 
                    position: 'relative',
                    zIndex: 1,
                    width: '60%',
                    minWidth: 300,
                    maxWidth: 600,
                    margin: '0 auto',
                    height: `calc(100dvh - ${120*factor}px - env(safe-area-inset-bottom))`,
                    minHeight: 300,
                    justifyContent: 'center',
                    alignItems: 'center',
                    elevation: 1,
                    padding: 2,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    msOverflowStyle: '-ms-autohiding-scrollbar',
                }}>
                
                {status === 'verifying' && (
                    <>
                        <Typography variant="h6">
                            You're account is being verified. Please wait.
                        </Typography>
                        <Box sx={{
                            width: '100%',
                            height: 100,
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: 2
                        }}>
                            <CircularProgress sx={{height: "100%"}} />
                        </Box>
                    </>
                )}
                {status === 'creating' && (
                    <Stack direction="column" spacing={1} alignItems='center' padding={1}>
                        <Typography variant="h6">Almost there!</Typography>
                        <Typography variant="subtitle1">
                            We need a little more information to create your account.
                        </Typography>
                        <Typography variant="subtitle2">{auth.user?.profile?.given_name} {auth.user?.profile?.family_name} | {auth.user?.profile?.email}</Typography>
                        <Divider sx={{width: '100%'}} />
                        <Stack direction='row' gap={1} flexWrap='wrap'>
                            <Stack direction='column' spacing={1.5} alignItems='center' padding={1}
                                sx={{
                                    flex: 1,
                                    minWidth: 270,
                                }}
                            >
                                <Typography variant="subtitle1">Required Fields</Typography>
                                <StyledTextboxInput
                                    appTheme={appTheme}
                                    factor={factor}
                                    fullWidth={true}
                                    label="Phone Number"
                                    value={userData.phoneNumber ? formatPhoneNumber(userData.phoneNumber) : ''}
                                    onChange={(e) => setUserData({...userData, phoneNumber: cleanPhoneNumber(e.target.value)})}
                                    onBlur={() => setUserData({...userData, phoneNumber: cleanPhoneNumber(userData.phoneNumber)})}
                                    error={userData.phoneNumber ? userData.phoneNumber.length !== 10 : undefined}
                                    helperText={userData.phoneNumber && userData.phoneNumber.length !== 10 ? 'Phone number must be 10 digits' : undefined}
                                    inputProps={{
                                        autoComplete: "tel",
                                        type: "tel",
                                        pattern: "[0-9]{3}-[0-9]{3}-[0-9]{4}"
                                    }}
                                />
                                <StyledTextboxInput
                                    appTheme={appTheme}
                                    factor={factor}
                                    fullWidth={true}
                                    label="Birth Date"
                                    type="date"
                                    value={userData.birthDate}
                                    onChange={(e) => setUserData({...userData, birthDate: e.target.value})}
                                    error={userData.birthDate ? new Date(userData.birthDate) > new Date() : undefined}
                                    inputProps={{
                                        autoComplete: "bday"
                                    }}
                                />
                                <StyledTextboxInput
                                    appTheme={appTheme}
                                    factor={factor}
                                    fullWidth={true}
                                    label="Address"
                                    value={userData.address}
                                    onChange={(e) => setUserData({...userData, address: e.target.value})}
                                    error={userData.address ? userData.address.length < 3 : undefined}
                                    inputProps={{
                                        autoComplete: "street-address"
                                    }}
                                />
                                <StyledTextboxInput
                                    appTheme={appTheme}
                                    factor={factor}
                                    fullWidth={true}
                                    label="City"
                                    value={userData.city}
                                    onChange={(e) => setUserData({...userData, city: e.target.value})}
                                    error={userData.city ? userData.city.length < 3 : undefined}
                                    inputProps={{
                                        autoComplete: "address-level2"
                                    }}
                                />
                                <StyledAutocompleteInput
                                    appTheme={appTheme}
                                    factor={factor}
                                    fullWidth={true}
                                    autoHighlight={true}
                                    autoComplete={true}
                                    label="State"
                                    value={userData.state}
                                    onChange={(e, newValue) => setUserData({...userData, state: newValue})}
                                    options={stateAbbreviations.states}
                                    renderInput={(params) => <TextField {...params} inputProps={{...params.inputProps, autoComplete: "address-level1"}} />}
                                />
                                <StyledTextboxInput
                                    appTheme={appTheme}
                                    factor={factor}
                                    fullWidth={true}
                                    label="Zip Code"
                                    type="number"
                                    value={userData.zipCode}
                                    onChange={(e) => setUserData({...userData, zipCode: e.target.value.slice(0,5)})}
                                    error={userData.zipCode ? userData.zipCode.length !== 5 : undefined}
                                    inputProps={{
                                        autoComplete: "postal-code"
                                    }}
                                />
                            </Stack>
                            <Stack direction='column' spacing={1.5} alignItems='center' padding={1}
                                sx={{
                                    flex: 1,
                                    minWidth: 270
                                }}
                            >
                                <Typography variant="subtitle1">Optional Fields</Typography>
                                <Typography variant="caption">
                                Gender is needed for standard gender based flights and tees. You are not required to select a gender, but without a gender, you may only be eligible for open divisions, flights and events, based on organizer regulations.
                                </Typography>
                                <StyledAutocompleteInput
                                    appTheme={appTheme}
                                    factor={factor}
                                    label="Gender"
                                    value={userData.gender}
                                    options={['Select Gender', 'Male', 'Female', 'Other']}
                                    onChange={(e, newValue) => setUserData({...userData, gender: newValue ? newValue : 'Select Gender'})}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                                <StyledTextboxInput
                                    appTheme={appTheme}
                                    factor={factor}
                                    fullWidth={true}
                                    label="GHIN Number"
                                    type="number"
                                    value={userData.ghinNumber}
                                    onChange={(e) => setUserData({...userData, ghinNumber: e.target.value})}
                                />
                            </Stack>
                        </Stack>
                        <Button variant="contained" color="primary" disabled={!submitAllowed}
                        onClick={createAccount}>Complete Account</Button>
                    </Stack>
                )}
                {status === 'connected' && (
                    <Stack direction="column" spacing={2} alignItems='center' padding={2}>
                        <Typography variant="h6">
                            Your account is verified!
                        </Typography>
                        <Typography variant="subtitle1">
                            You're all set up and ready to go.
                        </Typography>
                        {redirectURL !== '/' && (
                            <Typography variant="body2" color="text.secondary">
                                You'll be taken to: {redirectURL}
                            </Typography>
                        )}
                        <Button 
                            variant="contained" 
                            color="primary" 
                            size="large"
                            onClick={() => {
                                navigate(redirectURL);
                                localStorage.removeItem('redirectURL');
                            }}
                            sx={{ marginTop: 2 }}
                        >
                            Continue to {redirectURL === '/' ? 'Home' : 'Main Page'}
                        </Button>
                    </Stack>
                )}
                {status === 'error' && (
                    <>
                        <Typography variant="h6">
                            There was an error creating your account. Please refresh this page to try again.
                        </Typography>
                    </>
                )}
                </Paper>
            </Box>
            </PullToRefresh>
        </Stack>
    );
}
